import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { api, formatRelative, type MonitorStatus } from '../lib/api';
import { Button } from './ui';

const NAV = [
  { to: '/', label: 'Overview', end: true, permission: 'resource:read' },
  { to: '/machines', label: 'Instances', permission: 'resource:read' },
  { to: '/encoders', label: 'Encoders', permission: 'resource:read' },
  { to: '/magewell', label: 'Magewell', permission: 'magewell:read' },
  { to: '/devices', label: 'Devices', permission: 'device:read' },
  { to: '/intercom', label: 'Intercom', permission: 'intercom:manage' },
  { to: '/call', label: 'Call', permission: 'intercom:manage' },
  { to: '/recordings', label: 'Recordings', permission: 'recording:read' },
  { to: '/costs', label: 'Costs', permission: 'cost:read' },
  { to: '/admin', label: 'Admin', permission: 'user:read' },
];

export function Layout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [monitor, setMonitor] = useState<MonitorStatus | null>(null);

  // Poll the monitor's own health so a stale dashboard is visibly stale rather
  // than silently showing data from an hour ago.
  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const data = await api.get<{ monitor: MonitorStatus }>('/admin/system');
        if (!cancelled) setMonitor(data.monitor);
      } catch {
        // Ignore: the page itself will show an auth error if that is the cause.
      }
    }

    void poll();
    const timer = setInterval(() => void poll(), 30_000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const visibleNav = NAV.filter((item) => can(item.permission));
  const monitorUnhealthy = monitor && (monitor.consecutiveFailures > 0 || !monitor.running);

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-40 border-b border-grid bg-surface/95 backdrop-blur"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="page-gutter mx-auto flex h-14 max-w-[1600px] items-center gap-3 sm:gap-6">
          <NavLink to="/" className="shrink-0" aria-label="Engage Live Cloud Hub">
            <img src="/brand.png" alt="Engage Live Cloud Hub" className="h-7 w-auto sm:h-8" />
          </NavLink>

          {/* On a wide screen the pages sit beside the logo; on a phone they
              are a row of their own underneath, scrolling sideways. */}
          <nav className="hidden items-center gap-1 lg:flex">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-raised text-ink'
                      : 'text-ink-secondary hover:bg-raised hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {monitor && (
              <span
                className="hidden items-center gap-1.5 text-xs text-ink-muted sm:flex"
                title={
                  monitorUnhealthy
                    ? `Monitor is failing: ${monitor.lastError ?? 'not running'}`
                    : `Last scan ${formatRelative(monitor.lastRunAt)}, ${monitor.instanceCount} instances`
                }
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    background: monitorUnhealthy ? 'var(--color-critical)' : 'var(--color-good)',
                  }}
                />
                {monitorUnhealthy ? 'Monitor failing' : `Synced ${formatRelative(monitor.lastRunAt)}`}
              </span>
            )}

            {/* Not in NAV: that is gated by permission, and keys are for every
                signed-in user, whatever they may do. */}
            <NavLink
              to="/keys"
              className={({ isActive }) =>
                `hidden rounded-md px-2 py-1 text-xs transition-colors md:inline ${
                  isActive
                    ? 'bg-raised text-ink'
                    : 'text-ink-secondary hover:bg-raised hover:text-ink'
                }`
              }
              title="Keys for scripts and tools that call the hub for you"
            >
              API keys
            </NavLink>

            <NavLink
              to="/account"
              className={({ isActive }) =>
                `hidden rounded-md px-2 py-1 text-xs transition-colors md:inline ${
                  isActive
                    ? 'bg-raised text-ink'
                    : 'text-ink-secondary hover:bg-raised hover:text-ink'
                }`
              }
              title="Your account: password and two-factor"
            >
              {user?.name}
            </NavLink>

            <Button
              variant="ghost"
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
            >
              Sign out
            </Button>
          </div>
        </div>

        <nav className="page-gutter flex gap-1 overflow-x-auto pb-2 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `shrink-0 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  isActive ? 'bg-raised text-ink' : 'text-ink-secondary hover:bg-raised hover:text-ink'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <NavLink to="/keys" className={({ isActive }) => `shrink-0 rounded-md px-3 py-1.5 text-sm md:hidden ${isActive ? 'bg-raised text-ink' : 'text-ink-secondary'}`}>API keys</NavLink>
          <NavLink to="/account" className={({ isActive }) => `shrink-0 rounded-md px-3 py-1.5 text-sm md:hidden ${isActive ? 'bg-raised text-ink' : 'text-ink-secondary'}`}>Account</NavLink>
        </nav>

        {monitor && monitor.regionErrors.length > 0 && (
          <div
            className="page-gutter py-1.5 text-xs"
            style={{ background: 'color-mix(in srgb, var(--color-warning) 15%, transparent)' }}
          >
            ⚠ Could not scan {monitor.regionErrors.map((e) => e.region).join(', ')}. Instances in
            those regions may be missing or out of date.
          </div>
        )}
      </header>

      <main
        className="page-gutter mx-auto max-w-[1600px] py-4 sm:py-6"
        style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      >
        <Outlet />
      </main>
    </div>
  );
}
